// Only the initiating tab keeps the original admin credentials, in sessionStorage.
// Target JWTs and refresh tokens are never issued to the browser.
const API = '/user-management/api/v1/support';
const RETURN_KEY = 'actorSupportReturn';
export const isSupportToken = (token) => String(token || '').startsWith('support_');
export const supportSession = () => {
  try { return JSON.parse(localStorage.getItem('actorSupport') || 'null'); } catch { return null; }
};
export const authGeneration = () => localStorage.getItem('authSessionGeneration');
export const sameAuthGeneration = (generation) => generation === authGeneration();
export const canReturnToAdmin = () => {
  try { return JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null')?.token === supportSession()?.access_token && !!supportSession(); }
  catch { return false; }
};

const authKeys = ['token', 'refreshToken', 'isAdmin', 'userEmail', 'loginChannel', 'pmiCustomerSession'];
const snapshotAuth = () => Object.fromEntries(authKeys.map((key) => [key, localStorage.getItem(key)]));
const clearSession = () => {
  // Drop account-scoped caches as well as tokens before another identity loads.
  localStorage.clear();
};
const notifyTabs = () => localStorage.setItem('authSessionGeneration', `${Date.now()}-${Math.random()}`);

export const startActorSupport = async (actorId) => {
  const original = snapshotAuth();
  if (!original.token || isSupportToken(original.token)) throw new Error('Bitte als Admin anmelden.');
  const response = await fetch(`${API}/start`, {
    method: 'POST', headers: { Authorization: `Bearer ${original.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ actor_id: actorId }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(typeof data.detail === 'string' ? data.detail : 'Support-Anmeldung fehlgeschlagen.');
  }
  const data = await response.json();
  if (!isSupportToken(data.access_token) || !data.expires_at) throw new Error('Ungültige Support-Antwort.');
  // Do not replace a different login that completed while this request was pending.
  if (localStorage.getItem('token') !== original.token) throw new Error('Die Anmeldung hat sich geändert.');
  sessionStorage.clear();
  sessionStorage.setItem(RETURN_KEY, JSON.stringify({ original, token: data.access_token, actorId }));
  clearSession();
  localStorage.setItem('actorSupport', JSON.stringify(data));
  localStorage.setItem('token', data.access_token);
  notifyTabs();
  window.location.replace('/');
};

export const endActorSupport = async (restoreAdmin = true) => {
  const session = supportSession();
  const rawBackup = sessionStorage.getItem(RETURN_KEY);
  const backup = rawBackup ? JSON.parse(rawBackup) : null;
  if (!session) throw new Error('Keine Support-Sitzung vorhanden.');
  const response = await fetch(`${API}/end`, {
    method: 'POST', headers: { Authorization: `Bearer ${session.access_token}` },
  });
  // Unknown/expired credentials cannot authorize any further gateway request.
  if (!response.ok && response.status !== 401) throw new Error('Die Support-Sitzung konnte nicht beendet werden. Bitte erneut versuchen.');
  clearSession();
  if (restoreAdmin && backup?.token === session.access_token) {
    Object.entries(backup.original).forEach(([key, value]) => { if (value !== null) localStorage.setItem(key, value); });
  }
  sessionStorage.clear();
  notifyTabs();
  window.location.replace(restoreAdmin && backup?.token === session.access_token ? `/actors/${backup.actorId}` : '/login');
};

// A full document reload discards pending requests and in-memory account caches.
export const installSupportBoundary = () => {
  const onStorage = (event) => {
    if (event.key === 'authSessionGeneration') {
      sessionStorage.clear();
      window.location.reload();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};
